import {
  NavLink,
  useLocation,
  useBlocker,
  useNavigate,
} from "react-router-dom";
import { ArrowForwardIos } from "@mui/icons-material";
import { WrapperLoading } from "@src/shared/components";
import { useGetFilters } from "@src/entities/requestHH/model/hooks/useGetFilters/useGetFilters";
import { useGetRegions } from "@src/entities/requestHH/model/hooks/useGetRegions/useGetRegions";
import { requestHHStore } from "@src/entities/requestHH";
import { getDefaultValues } from "@src/features/creatingSearchHH/ui/constans";
import { useEffect, useMemo, useRef, useState } from "react";
import { CreatingSearchHH } from "@src/features/creatingSearchHH";
import { useStructureVacancy } from "@src/entities/requestHH/model/hooks/useStructureVacancy/useStructureVacancy";
import { VacancyDescription } from "./VacancyDescription/VacancyDescription";
import {
  IStructureVacancyRequestDto,
  IStructureVacancyResponseData,
} from "@src/entities/requestHH/api/interfaces/jobRequestDto";
import {
  emptyGrades,
  mapStructuredVacancyDtoToForm,
} from "./mappers/mapStructuredVacancyDtoToForm";
import { ExitStructuredFormModal } from "@src/shared/components/ExitStructuredFormModal/ExitStructuredFormModal";
import { useCancelStructuredVacancy } from "@src/entities/requestHH/model/hooks/useCancelStructuredVacancy/useCancelStructuredVacancy";
import { useUserInfoMeStore } from "@src/entities/user/model/store/userInfoMeStore";
import { flushSync } from "react-dom";
import { useGetUserInfoMe } from "@src/entities/user";

import styles from "./CreatingSearchRequestHH.module.scss";

type TStep = 1 | 2;

type TCreateRequestLocationState = {
  startNewRequest?: boolean;
  startNewRequestNonce?: number;
};

export function CreatingSearchRequestHH() {
  const location = useLocation();
  const locationState = location.state as TCreateRequestLocationState | null;
  const startNewRequestNonce = locationState?.startNewRequestNonce;
  const isChangeQuery = location.pathname.includes("/change-search-query");
  const isNewRequest = !isChangeQuery;
  const [step, setStep] = useState<TStep>(isNewRequest ? 1 : 2);
  const [sourceVacancy, setSourceVacancy] =
    useState<IStructureVacancyRequestDto | null>(null);
  const [structuredVacancy, setStructuredVacancy] =
    useState<IStructureVacancyResponseData | null>(null);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [isRequestCreated, setIsRequestCreated] = useState(false);
  const navigate = useNavigate();
  const cancelStructuredVacancyMutation = useCancelStructuredVacancy();
  const userMe = useUserInfoMeStore((state) => state.user);
  const { refetch } = useGetUserInfoMe();

  const structureVacancyMutation = useStructureVacancy();

  const shouldBlockNavigation =
    step === 2 && Boolean(structuredVacancy) && !isRequestCreated;

  const blocker = useBlocker(shouldBlockNavigation);

  const {
    isPending: isGetRegionsPending,
    isError: isGetRegionsErrors,
    data: regionsList,
  } = useGetRegions();

  const {
    isPending: isGetFiltersPending,
    isError: isGetFiltersErrors,
    data: filters,
  } = useGetFilters();

  const requestHHParams = requestHHStore((state) => state.requestHHParams);
  const clearRequestHHParams = requestHHStore(
    (state) => state.clearRequestHHParams,
  );

  const targetRef = useRef<HTMLDivElement>(null);
  const lastStartNewRequestNonceRef = useRef<number | null>(null);

  const baseDefaultValue = useMemo(() => {
    return getDefaultValues(
      isNewRequest,
      regionsList?.data || [],
      filters?.data.experience_levels || [],
      filters?.data.education_types || [],
      filters?.data.work_format || [],
      filters?.data.job_search_status || [],
      filters?.data.relocation_types || [],
      filters?.data.grades || emptyGrades,
      requestHHParams,
    );
  }, [isNewRequest, regionsList?.data, filters?.data, requestHHParams]);

  const defaultValue = useMemo(() => {
    if (step === 2 && structuredVacancy && sourceVacancy && filters?.data) {
      return mapStructuredVacancyDtoToForm({
        baseValues: baseDefaultValue,
        structuredVacancy,
        areaOptions: regionsList?.data || [],
        experienceOptions: filters.data.experience_levels || [],
        educationOptions: filters.data.education_types || [],
        workFormatOptions: filters.data.work_format || [],
        jobStatusOptions: filters.data.job_search_status || [],
        resumeRelocationOptions: filters.data.relocation_types || [],
        gradeOptions: filters.data.grades || [],
      });
    }

    return baseDefaultValue;
  }, [
    baseDefaultValue,
    step,
    structuredVacancy,
    sourceVacancy,
    filters?.data,
    regionsList?.data,
  ]);

  const handleStructureVacancy = async (
    values: IStructureVacancyRequestDto,
  ) => {
    const response = await structureVacancyMutation.mutateAsync(values);

    setSourceVacancy(values);
    setStructuredVacancy(response.data);
    setStep(2);
  };

  const isPending =
    isGetFiltersPending ||
    isGetRegionsPending ||
    structureVacancyMutation.isPending;

  const isError =
    isGetFiltersErrors ||
    isGetRegionsErrors ||
    structureVacancyMutation.isError;

  useEffect(() => {
    if (targetRef && !isNewRequest) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    }

    return () => clearRequestHHParams();
  }, []);

  useEffect(() => {
    if (!startNewRequestNonce) {
      return;
    }

    if (lastStartNewRequestNonceRef.current === startNewRequestNonce) {
      return;
    }

    lastStartNewRequestNonceRef.current = startNewRequestNonce;

    clearRequestHHParams();

    setStep(1);
    setSourceVacancy(null);
    setStructuredVacancy(null);
    setIsExitModalOpen(false);
    setIsRequestCreated(false);
  }, [startNewRequestNonce, clearRequestHHParams]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      setIsExitModalOpen(true);
    }
  }, [blocker.state]);

  const handleConfirmCloseForm = async () => {
    try {
      if (structuredVacancy && userMe?.id) {
        await cancelStructuredVacancyMutation.mutateAsync();
        refetch();
      }

      setIsExitModalOpen(false);

      if (blocker.state === "blocked") {
        blocker.proceed();
        return;
      }

      navigate("/");
    } catch (_) {
      setIsExitModalOpen(false);

      if (blocker.state === "blocked") {
        blocker.proceed();
        return;
      }

      navigate("/");
    }
  };
  const handleCancelCloseForm = () => {
    setIsExitModalOpen(false);

    if (blocker.state === "blocked") {
      blocker.reset();
    }
  };

  const handleRequestCreated = () => {
    flushSync(() => {
      setIsRequestCreated(true);
    });
  };

  return (
    <div className={styles.wrapper}>
      <WrapperLoading isLoading={isPending} isError={isError || !defaultValue}>
        <div className={styles.placeInfo}>
          <NavLink to={"/"} className={styles.mainPageLink}>
            Подбор кандидатов на вакансию
          </NavLink>
          <ArrowForwardIos sx={{ width: "10px", height: "10px" }} />
          <span className={styles.descriptionRequest}>Описание запроса</span>
        </div>
        <h1 className={styles.title}>Введите описание запроса</h1>
        <div className={styles.content}>
          {step === 1 && (
            <VacancyDescription
              isLoading={isPending}
              onSubmit={handleStructureVacancy}
            />
          )}
          {step === 2 && (
            <CreatingSearchHH
              defaultValue={defaultValue}
              onRequestCreated={handleRequestCreated}
            />
          )}
        </div>
        <div ref={targetRef} />
        <ExitStructuredFormModal
          open={isExitModalOpen}
          isLoading={cancelStructuredVacancyMutation.isPending}
          onClose={handleCancelCloseForm}
          onConfirm={handleConfirmCloseForm}
        />
      </WrapperLoading>
    </div>
  );
}
